"use client";

import { useCallback, useMemo, useRef } from "react";

/**
 * A snapshot of the chat view that was visible when an async flow started.
 * Its UI writes are applied only while that view is still the active one.
 */
export interface ViewScope {
   /** Runs `apply` only if the captured view is still visible. */
   run: (apply: () => void) => void;
   /** Whether the captured view is still visible. */
   isActive: () => boolean;
}

/**
 * Guards shared chat UI state (history, streaming text) against out-of-order
 * writes when the user switches chats while a generation is in flight.
 *
 * The visible chat is tagged with a monotonically increasing sequence. Each
 * async flow (submission, reconnect, response persistence, history load)
 * captures a {@link ViewScope} when it starts and applies its UI writes only
 * while that scope is still the active view.
 */
export interface HistoryViewGuard {
   /** Advances to a new view. Call on a genuine chat switch. */
   advanceView: () => void;
   /** Captures the currently visible view for guarding later writes. */
   capture: () => ViewScope;
}

export function useHistoryViewGuard(): HistoryViewGuard {
   const viewSeqRef = useRef(0);

   const advanceView = useCallback(() => {
      viewSeqRef.current += 1;
   }, []);

   const capture = useCallback((): ViewScope => {
      const seq = viewSeqRef.current;
      const isActive = () => seq === viewSeqRef.current;
      return {
         isActive,
         run: (apply) => {
            if (isActive()) apply();
         }
      };
   }, []);

   return useMemo(() => ({ advanceView, capture }), [advanceView, capture]);
}
